import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PhoneForwarded, Loader2, User, Bot } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import {
  fetchSessionTranscript,
  transferToHuman,
  type SessionTranscript,
} from '@/lib/powerDialer';

// -------------------------------------------------------------------------
// VoiceSessionPage — Story 69.3 (Blended Mode)
// Route: /voice/sessions/:id
//
// Shows the live session transcript and a "Take over" floating button that
// triggers the AI→human handoff.
// -------------------------------------------------------------------------

export function VoiceSessionPage() {
  const { t } = useTranslation();
  const { id: sessionId } = useParams<{ id: string }>();
  const [transcript, setTranscript] = useState<SessionTranscript | null>(null);
  const [loading, setLoading] = useState(true);
  const [transferring, setTransferring] = useState(false);
  const [transferError, setTransferError] = useState<string | null>(null);
  const [transferred, setTransferred] = useState(false);
  const [repPhone, setRepPhone] = useState('');
  const [showTransferDialog, setShowTransferDialog] = useState(false);

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;

    const loadTranscript = async () => {
      try {
        const t = await fetchSessionTranscript(sessionId);
        if (!cancelled) setTranscript(t);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadTranscript();

    // Poll for transcript updates while session is active
    const interval = setInterval(async () => {
      if (!cancelled && !transferred) {
        const updated = await fetchSessionTranscript(sessionId).catch(() => null);
        if (!cancelled && updated) setTranscript(updated);
      }
    }, TRANSCRIPT_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [sessionId, transferred]);

  async function handleTransfer() {
    if (!sessionId || !repPhone.trim()) return;
    setTransferring(true);
    setTransferError(null);
    try {
      const result = await transferToHuman(sessionId, repPhone);
      if (result.success) {
        setTransferred(true);
        setShowTransferDialog(false);
      } else {
        setTransferError(t('blendedMode.take_over_error', { error: result.error ?? 'unknown' }));
      }
    } catch {
      setTransferError(t('blendedMode.take_over_error', { error: 'network_error' }));
    } finally {
      setTransferring(false);
    }
  }

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <h1 className="text-xl font-semibold mb-1">{t('blendedMode.transcript_title')}</h1>
      <p className="text-muted-foreground text-sm mb-6">
        {t('app.name')} AI Agent — Session {sessionId?.slice(0, 8)}
      </p>

      {transferred && (
        <div className="mb-4 rounded-lg bg-green-950/40 border border-green-800 text-green-200 px-4 py-3 text-sm">
          {t('blendedMode.take_over_success')}
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground py-12">
          <Loader2 className="h-5 w-5 animate-spin" />
          {t('common.loading')}
        </div>
      ) : !transcript || transcript.lines.length === 0 ? (
        <p className="text-muted-foreground py-12 text-center">{t('blendedMode.transcript_empty')}</p>
      ) : (
        <div className="space-y-3">
          {transcript.lines.map((line, i) => (
            <TranscriptLine key={i} speaker={line.speaker} text={line.text} />
          ))}
        </div>
      )}

      {/* Floating "Take over" button — Story 69.3 */}
      {!transferred && (
        <div className="fixed bottom-8 right-8">
          <Button
            onClick={() => setShowTransferDialog(true)}
            className="rounded-full shadow-lg px-5 py-3 text-base"
          >
            <PhoneForwarded className="h-5 w-5 mr-2" />
            {t('blendedMode.take_over')}
          </Button>
        </div>
      )}

      {/* Transfer confirmation dialog */}
      {showTransferDialog && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-background rounded-lg shadow-xl p-6 w-full max-w-sm">
            <h2 className="text-lg font-semibold mb-2">{t('blendedMode.take_over')}</h2>
            <p className="text-sm text-muted-foreground mb-4">
              {t('blendedMode.take_over_confirm')}
            </p>

            <label className="block text-sm font-medium mb-1">
              {t('common.phone') ?? 'Your phone number (E.164)'}
            </label>
            <input
              className="w-full border rounded px-3 py-2 text-sm mb-4 font-mono"
              value={repPhone}
              onChange={(e) => setRepPhone(e.target.value)}
              placeholder="+13055550100"
            />

            {transferError && (
              <p className="text-destructive text-sm mb-3">{transferError}</p>
            )}

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowTransferDialog(false)}>
                {t('common.cancel')}
              </Button>
              <Button
                onClick={handleTransfer}
                disabled={transferring || !repPhone.trim()}
              >
                {transferring ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-1" />
                    {t('common.loading')}
                  </>
                ) : (
                  t('blendedMode.take_over')
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TranscriptLine({ speaker, text }: { speaker: string; text: string }) {
  const { t } = useTranslation();
  const isAgent = speaker.toUpperCase() === 'AGENT';
  const displayName = t(`blendedMode.speaker_${speaker.toUpperCase()}`, {
    defaultValue: speaker,
  });

  return (
    <div
      className={`flex gap-3 ${isAgent ? 'flex-row' : 'flex-row-reverse'}`}
    >
      <div
        className={`flex-shrink-0 h-7 w-7 rounded-full flex items-center justify-center text-white text-xs ${
          isAgent ? 'bg-primary' : 'bg-muted-foreground'
        }`}
      >
        {isAgent ? <Bot className="h-4 w-4" /> : <User className="h-4 w-4" />}
      </div>
      <div
        className={`rounded-lg px-3 py-2 text-sm max-w-[75%] ${
          isAgent
            ? 'bg-primary/10 text-primary-foreground'
            : 'bg-muted text-foreground'
        }`}
      >
        <span className="text-xs font-medium text-muted-foreground block mb-0.5">
          {displayName}
        </span>
        {text}
      </div>
    </div>
  );
}

const TRANSCRIPT_POLL_INTERVAL_MS = 5_000;
